import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faInfoCircle,
  faExclamationTriangle,
  faTag,
  faEllipsisH,
  faTimes,
  faCheckDouble,
  faInbox,
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import notificationService from '../services/notificationService';
import './NotificationBell.css';

/* ─── helpers ─────────────────────────────────────────────────────────── */

const TYPE_ICONS = {
  Info:      { icon: faInfoCircle,           cls: 'info' },
  Warning:   { icon: faExclamationTriangle,  cls: 'warning' },
  Promotion: { icon: faTag,                  cls: 'promotion' },
  Other:     { icon: faEllipsisH,            cls: 'other' },
};

const TYPE_BADGE_CLS = {
  Info:      'badge-info',
  Warning:   'badge-warning',
  Promotion: 'badge-promotion',
  Other:     'badge-other',
};

const HISTORY_TYPES = ['All', 'Info', 'Warning', 'Promotion', 'Other'];

function typeInfo(type) {
  return TYPE_ICONS[type] || TYPE_ICONS.Other;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now  = new Date();
  const diffMs = now - date;
  const diffMins  = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays  = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1)  return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7)  return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/* ─── Main component ──────────────────────────────────────────────────── */

export default function NotificationBell() {
  const { isDarkMode } = useTheme();
  const { isAuthenticated } = useAuth();

  // panel open / tab state
  const [open, setOpen]     = useState(false);
  const [activeTab, setActiveTab] = useState('unread'); // 'unread' | 'history'
  const [historyFilter, setHistoryFilter] = useState('All');

  // data
  const [unread,  setUnread]  = useState([]);
  const [history, setHistory] = useState([]);

  // loading / error
  const [loadingUnread,  setLoadingUnread]  = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // detail modal
  const [detailItem, setDetailItem] = useState(null);

  const wrapperRef = useRef(null);

  /* ── Fetch unread ──────────────────────────────────────────────────── */
  const fetchUnread = useCallback(async () => {
    try {
      setLoadingUnread(true);
      const data = await notificationService.getUnread();
      setUnread(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[NotificationBell] Failed to fetch unread notifications:', err);
    } finally {
      setLoadingUnread(false);
    }
  }, []);

  /* ── Fetch history with optional type filter ───────────────────────── */
  const fetchHistory = useCallback(async (typeFilter) => {
    try {
      setLoadingHistory(true);
      const params = typeFilter && typeFilter !== 'All'
        ? { Type: typeFilter }
        : {};
      const data = await notificationService.getHistory(params);
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[NotificationBell] Failed to fetch notification history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  /* ── Fetch on mount / when Dashboard page loads ──────────────────── */
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchUnread();
  }, [isAuthenticated, fetchUnread]);

  /* ── Load history when History tab is opened or filter changes ─────── */
  useEffect(() => {
    if (open && activeTab === 'history') {
      fetchHistory(historyFilter);
    }
  }, [open, activeTab, historyFilter, fetchHistory]);

  /* ── Refresh unread list when panel opens ──────────────────────────── */
  useEffect(() => {
    if (open && activeTab === 'unread') {
      fetchUnread();
    }
  }, [open, activeTab, fetchUnread]);

  /* ── Close on outside click ────────────────────────────────────────── */
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  /* ── Mark single notification as read ─────────────────────────────── */
  const handleMarkRead = useCallback(async (item, e) => {
    if (e) e.stopPropagation();
    if (item.isRead) return;

    try {
      await notificationService.markRead(item.id);
      // Optimistically update local state
      const markRead = (list) =>
        list.map((n) =>
          n.id === item.id ? { ...n, isRead: true, readDate: new Date().toISOString() } : n
        );
      setUnread((prev) => prev.filter((n) => n.id !== item.id));
      setHistory((prev) => markRead(prev));
    } catch {
      // ignore
    }
  }, []);

  /* ── Mark all unread as read ───────────────────────────────────────── */
  const handleMarkAllRead = useCallback(async () => {
    const unreadItems = unread.filter((n) => !n.isRead);
    if (unreadItems.length === 0) return;

    // Fire all requests concurrently
    await Promise.allSettled(
      unreadItems.map((n) => notificationService.markRead(n.id))
    );
    setUnread([]);
    setHistory((prev) =>
      prev.map((n) =>
        unreadItems.some((u) => u.id === n.id)
          ? { ...n, isRead: true, readDate: new Date().toISOString() }
          : n
      )
    );
  }, [unread]);

  /* ── Open detail modal ─────────────────────────────────────────────── */
  const openDetail = useCallback(
    (item) => {
      setDetailItem(item);
      // Mark as read automatically when opened
      if (!item.isRead) {
        handleMarkRead(item);
      }
    },
    [handleMarkRead]
  );

  /* ── Derived values ────────────────────────────────────────────────── */
  const unreadCount = unread.length;
  const badgeLabel  = unreadCount > 99 ? '99+' : unreadCount;

  /* ─── Render ─────────────────────────────────────────────────────────── */
  const panelCls = `notification-panel${isDarkMode ? ' dark-panel' : ''}`;
  const modalCls = `notification-detail-modal${isDarkMode ? ' dark-modal' : ''}`;

  return (
    <div className="notification-bell-wrapper" ref={wrapperRef}>
      {/* Bell button */}
      <button
        className="notification-bell-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        title="Notifications"
        style={{ color: isDarkMode ? '#ffffff' : '#ffffff' }}
      >
        <FontAwesomeIcon icon={faBell} className="notification-bell-icon" />
        {unreadCount > 0 && (
          <span className="notification-badge" aria-hidden="true">
            {badgeLabel}
          </span>
        )}
      </button>
      {unreadCount > 0 && (
        <span className="notification-new-message-label" aria-hidden="true">
          New Message
        </span>
      )}

      {/* Dropdown panel */}
      {open && (
        <div className={panelCls} role="dialog" aria-label="Notifications">
          {/* Panel header */}
          <div className="notification-panel-header">
            <span className="notification-panel-title">Notifications</span>
            {unreadCount > 0 && (
              <button
                className="notification-mark-all-btn"
                onClick={handleMarkAllRead}
                title="Mark all as read"
              >
                <FontAwesomeIcon icon={faCheckDouble} style={{ marginRight: 4 }} />
                Mark all read
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="notification-tabs" role="tablist">
            <button
              className={`notification-tab${activeTab === 'unread' ? ' active' : ''}`}
              role="tab"
              aria-selected={activeTab === 'unread'}
              onClick={() => setActiveTab('unread')}
            >
              Unread
              {unreadCount > 0 && (
                <span className="notification-tab-badge">{badgeLabel}</span>
              )}
            </button>
            <button
              className={`notification-tab${activeTab === 'history' ? ' active' : ''}`}
              role="tab"
              aria-selected={activeTab === 'history'}
              onClick={() => setActiveTab('history')}
            >
              History
            </button>
          </div>

          {/* History type filter */}
          {activeTab === 'history' && (
            <div className="notification-filter-bar">
              <span className="notification-filter-label">Filter:</span>
              {HISTORY_TYPES.map((t) => (
                <button
                  key={t}
                  className={`notification-filter-chip${historyFilter === t ? ' active' : ''}`}
                  onClick={() => setHistoryFilter(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          )}

          {/* Notification list */}
          <div className="notification-list" role="list">
            {activeTab === 'unread' ? (
              loadingUnread ? (
                <div className="notification-loading">Loading&hellip;</div>
              ) : unread.length === 0 ? (
                <div className="notification-empty">
                  <FontAwesomeIcon icon={faInbox} className="notification-empty-icon" />
                  <span>No unread notifications</span>
                </div>
              ) : (
                unread.map((n) => (
                  <NotificationItem
                    key={n.id}
                    item={n}
                    isDarkMode={isDarkMode}
                    onClick={() => openDetail(n)}
                    onMarkRead={(e) => handleMarkRead(n, e)}
                  />
                ))
              )
            ) : (
              loadingHistory ? (
                <div className="notification-loading">Loading&hellip;</div>
              ) : history.length === 0 ? (
                <div className="notification-empty">
                  <FontAwesomeIcon icon={faInbox} className="notification-empty-icon" />
                  <span>No notifications</span>
                </div>
              ) : (
                history.map((n) => (
                  <NotificationItem
                    key={n.id}
                    item={n}
                    isDarkMode={isDarkMode}
                    onClick={() => openDetail(n)}
                    onMarkRead={(e) => handleMarkRead(n, e)}
                  />
                ))
              )
            )}
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detailItem && (
        <NotificationDetail
          item={detailItem}
          isDarkMode={isDarkMode}
          onClose={() => setDetailItem(null)}
        />
      )}
    </div>
  );
}

/* ─── NotificationItem sub-component ─────────────────────────────────── */

function NotificationItem({ item, isDarkMode, onClick, onMarkRead }) {
  const { icon, cls } = typeInfo(item.type);
  const typeCls = item.type ? item.type.toLowerCase() : 'other';

  return (
    <div
      role="listitem"
      className={`notification-item${!item.isRead ? ' unread' : ''} type-${typeCls}`}
      onClick={onClick}
      title={item.isRead ? 'Click to view' : 'Click to view and mark as read'}
    >
      {/* Type icon */}
      <div className={`notification-type-icon ${cls}`}>
        <FontAwesomeIcon icon={icon} />
      </div>

      {/* Content */}
      <div className="notification-item-body">
        <div className="notification-item-header">
          <span className="notification-item-title">
            {item.title || 'Notification'}
          </span>
          <span className="notification-item-date">
            {formatDate(item.deliveredDate)}
          </span>
        </div>
        <button
          className="notification-item-open-btn"
          onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
          title="Open notification"
        >
          Open
        </button>
      </div>

      {/* Unread indicator dot */}
      {!item.isRead && (
        <div
          className={`notification-unread-dot type-${typeCls}`}
          title="Unread"
          onClick={onMarkRead}
        />
      )}
    </div>
  );
}

/* ─── NotificationDetail sub-component ──────────────────────────────── */

function NotificationDetail({ item, isDarkMode, onClose }) {
  const { icon, cls } = typeInfo(item.type);
  const typeCls   = item.type ? item.type.toLowerCase() : 'other';
  const badgeCls  = TYPE_BADGE_CLS[item.type] || 'badge-other';
  const modalCls  = `notification-detail-modal${isDarkMode ? ' dark-modal' : ''}`;

  // Close on Escape key
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="notification-detail-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={item.title || 'Notification detail'}
    >
      <div className={modalCls}>
        {/* Close button */}
        <button
          className="notification-detail-close"
          onClick={onClose}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        {/* Type badge */}
        <div className={`notification-detail-type-badge ${badgeCls}`}>
          <FontAwesomeIcon icon={icon} />
          <span>{item.type || 'Other'}</span>
        </div>

        {/* Title */}
        <h2 className="notification-detail-title">
          {item.title || 'Notification'}
        </h2>

        {/* Date */}
        <div className="notification-detail-date">
          Received: {item.deliveredDate
            ? new Date(item.deliveredDate).toLocaleString()
            : 'Unknown'}
          {item.readDate && (
            <span style={{ marginLeft: 12, color: '#27ae60' }}>
              &bull; Read: {new Date(item.readDate).toLocaleString()}
            </span>
          )}
        </div>

        {/* Message */}
        <div
          className="notification-detail-message"
          dangerouslySetInnerHTML={{ __html: item.message || '<p>No message content.</p>' }}
        />
      </div>
    </div>
  );
}
